import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import * as RealtimeActions from '@app/shared/store/realtime/realtime.actions';
import { UserActivityService } from '../services/user-activity.service';
import * as UserActivityActions from './user-activity.actions';
import { userActivityFeature } from './user-activity.reducer';

/**
 * The online list is a snapshot from notification-management plus the live
 * DEVICE_CONNECTED / DEVICE_DISCONNECTED the reducer applies on top. A fresh
 * DEVICE_LIST means the socket (re)identified — events may have been missed
 * while it was down — so a list already on screen is fetched again.
 */
@Injectable()
export class UserActivityEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(UserActivityService);
  private readonly translate = inject(TranslateService);

  readonly loadOnlineSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActivityActions.loadOnlineSessions),
      switchMap(() =>
        this.service.getOnlineSessions().pipe(
          map(response =>
            UserActivityActions.loadOnlineSessionsSuccess({
              sessions: response.data ?? [],
            })
          ),
          catchError(error =>
            of(
              UserActivityActions.loadOnlineSessionsFailure({
                error: extractErrorMessage(
                  error,
                  this.translate.instant('userActivity.online.loadError')
                ),
              })
            )
          )
        )
      )
    )
  );

  readonly resyncAfterReconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealtimeActions.realtimeDeviceListReceived),
      withLatestFrom(this.store.select(userActivityFeature.selectLoaded)),
      filter(([, loaded]) => loaded),
      map(() => UserActivityActions.loadOnlineSessions())
    )
  );
}
